import AttachmentService from '../services/attachment.service'

export interface PendingInlineImage {
    pendingId: string
    file: File
}

export interface PersistedInlineImage {
    id: number | string
    file_url?: string
    filename?: string
    [key: string]: any
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const escapeHtmlAttribute = (value: unknown) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

const hasAttributeValue = (tag: string, name: string, value: string) => {
    const pattern = new RegExp(`\\s${escapeRegExp(name)}=(?:"${escapeRegExp(value)}"|'${escapeRegExp(value)}')`, 'i')
    return pattern.test(tag)
}

const removeAttribute = (tag: string, name: string) => (
    tag.replace(new RegExp(`\\s${escapeRegExp(name)}=(?:"[^"]*"|'[^']*')`, 'gi'), '')
)

const setAttribute = (tag: string, name: string, value: unknown) => {
    const withoutAttribute = removeAttribute(tag, name)
    return withoutAttribute.replace(/^<img\b/i, `<img ${name}="${escapeHtmlAttribute(value)}"`)
}

const mapImageTags = (html: string, mapper: (tag: string) => string) => (
    String(html || '').replace(/<img\b[^>]*>/gi, mapper)
)

export const hasPendingInlineImages = (html: string) => (
    /<img\b[^>]*\sdata-pending-image-id=(?:"[^"]+"|'[^']+')[^>]*>/i.test(String(html || ''))
)

export const hasPendingInlineImage = (html: string, pendingId: string) => (
    html.includes(`data-pending-image-id="${pendingId}"`)
)

export const removePendingInlineImages = (html: string) => (
    mapImageTags(html, (tag) => /\sdata-pending-image-id=(?:"[^"]+"|'[^']+')/i.test(tag) ? '' : tag)
)

export const replacePendingInlineImage = (
    html: string,
    pendingId: string,
    attachment: PersistedInlineImage,
) => mapImageTags(html, (tag) => {
    if (!hasAttributeValue(tag, 'data-pending-image-id', pendingId)) {
        return tag
    }

    const stableSource = attachment.file_url || `/api/attachments/${attachment.id}/file`
    let nextTag = removeAttribute(tag, 'data-pending-image-id')
    nextTag = setAttribute(nextTag, 'data-attachment-id', attachment.id)
    nextTag = setAttribute(nextTag, 'src', stableSource)

    return nextTag
})

export const persistPendingInlineImages = async (
    taskId: number,
    html: string,
    pendingImages: PendingInlineImage[],
) => {
    let resolvedHtml = html

    for (const pendingImage of pendingImages) {
        const attachment = await AttachmentService.upload(taskId, pendingImage.file) as PersistedInlineImage

        if (attachment?.id === null || attachment?.id === undefined) {
            throw new Error('Uploaded inline image has no attachment id')
        }

        resolvedHtml = replacePendingInlineImage(resolvedHtml, pendingImage.pendingId, attachment)
    }

    return resolvedHtml
}
